import React, { useState } from 'react';
import styled from 'styled-components';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { adminApi, extractErrorMessage, formatCurrency, formatDate } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  SubscriptionStatus,
  InvoiceStatus,
  BillingPeriod,
  MerchantInvoiceDto,
  MerchantPlanOptionDto,
  MerchantInvoiceChargeDto,
} from '@kardume/types';
import {
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Table,
  TableContainer,
  Badge,
  Button,
  Modal,
  ConfirmModal,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1.25rem;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  span {
    font-size: 0.75rem;
    color: ${(p) => p.theme.textMuted};
  }
  strong {
    font-size: 1.05rem;
    color: ${(p) => p.theme.textPrimary};
  }
  small {
    font-size: 0.78rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const InvoiceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${(p) => p.theme.primary};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1.1rem;
`;

const PlanList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const PlanOption = styled.button<{ $selected: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: ${(p) => p.theme.radius};
  border: 1.5px solid ${(p) => (p.$selected ? p.theme.primary : p.theme.border)};
  background: ${(p) => (p.$selected ? `${p.theme.primary}0d` : p.theme.surface)};
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.55 : 1)};
  text-align: left;
  width: 100%;

  strong {
    display: block;
    font-size: 0.95rem;
    color: ${(p) => p.theme.textPrimary};
  }
  span {
    font-size: 0.8rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const PlanCheck = styled.span<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1.5px solid ${(p) => (p.$selected ? p.theme.primary : p.theme.border)};
  background: ${(p) => (p.$selected ? p.theme.primary : 'transparent')};
  color: #fff;
`;

const Hint = styled.p`
  font-size: 0.82rem;
  color: ${(p) => p.theme.textMuted};
  margin: 0.9rem 0 0;
  line-height: 1.5;
`;

const PixBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
  text-align: center;
`;

const PixImage = styled.img`
  width: 200px;
  height: 200px;
  border-radius: ${(p) => p.theme.radius};
  border: 1px solid ${(p) => p.theme.border};
`;

const PixCodeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;

  code {
    flex: 1;
    display: block;
    padding: 0.6rem 0.75rem;
    border-radius: ${(p) => p.theme.radius};
    background: ${(p) => p.theme.lightGray};
    color: ${(p) => p.theme.textPrimary};
    font-size: 0.78rem;
    word-break: break-all;
    text-align: left;
  }
`;

const BILLING_PERIOD_LABELS: Record<BillingPeriod, string> = {
  MONTHLY: 'Mensal',
  QUARTERLY: 'Trimestral',
  YEARLY: 'Anual',
};

/** Map a subscription status to a Badge tone. */
function subscriptionTone(status: SubscriptionStatus): 'success' | 'warning' | 'danger' {
  if (status === 'ACTIVE') return 'success';
  if (status === 'TRIALING') return 'warning';
  return 'danger';
}

const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  ACTIVE: 'Ativa',
  TRIALING: 'Em teste',
  PAST_DUE: 'Em atraso',
  CANCELED: 'Cancelada',
};

/** Map an invoice status to a Badge tone. */
function invoiceTone(status: InvoiceStatus): 'success' | 'warning' | 'danger' | 'secondary' {
  if (status === 'PAID') return 'success';
  if (status === 'OPEN') return 'warning';
  if (status === 'CANCELED') return 'secondary';
  return 'danger';
}

const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  PAID: 'Paga',
  OPEN: 'Em aberto',
  OVERDUE: 'Vencida',
  CANCELED: 'Cancelada',
};

const BILLING_ERROR_MESSAGES: Record<string, string> = {
  SUBSCRIPTION_SAME_PLAN: 'A assinatura já está neste plano.',
  SUBSCRIPTION_CANCELED: 'A assinatura está cancelada - não é possível trocar de plano ou pagar faturas.',
  INVOICE_ALREADY_PAID: 'Esta fatura já está paga.',
  INVOICE_CANCELED: 'Esta fatura foi cancelada.',
  MERCHANT_DOCUMENT_REQUIRED:
    'Cadastre o CNPJ da loja (ou o CPF do responsável) na tela Perfil antes de gerar a cobrança.',
};

const BillingPage: React.FC = () => {
  const me = useAsync(() => adminApi.getMe(), []);
  const subscription = useAsync(() => adminApi.getSubscription(), []);
  const invoices = useAsync(() => adminApi.listInvoices({ size: 100 }), []);
  const { showNotification } = useNotification();

  const canManage = me.data?.user.effectivePermissions.includes('ROLE_MERCHANT_FINANCE_MANAGE') ?? false;

  // --- Trocar de plano ---------------------------------------------------
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [plans, setPlans] = useState<MerchantPlanOptionDto[] | null>(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [changingPlan, setChangingPlan] = useState(false);

  const openPlanModal = async () => {
    setPlanModalOpen(true);
    setSelectedPlanId(null);
    if (!plans) {
      setPlansLoading(true);
      try {
        const data = await adminApi.getBillingPlans();
        setPlans(data);
      } catch (err) {
        showNotification(extractErrorMessage(err, 'Não foi possível carregar os planos.'), 'error');
        setPlanModalOpen(false);
      } finally {
        setPlansLoading(false);
      }
    }
  };

  const confirmChangePlan = async () => {
    if (!selectedPlanId) return;
    setChangingPlan(true);
    try {
      const updated = await adminApi.changePlan(selectedPlanId);
      subscription.setData(updated);
      setPlanModalOpen(false);
      showNotification(
        'Plano alterado. A mudança vale a partir do próximo ciclo - o valor deste ciclo não muda.',
        'success'
      );
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível trocar de plano.', BILLING_ERROR_MESSAGES), 'error');
    } finally {
      setChangingPlan(false);
    }
  };

  // --- Cancelar assinatura -------------------------------------------------
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const confirmCancel = async () => {
    setCanceling(true);
    try {
      const updated = await adminApi.cancelSubscription();
      subscription.setData(updated);
      setCancelModalOpen(false);
      showNotification('Assinatura cancelada.', 'success');
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível cancelar a assinatura.', BILLING_ERROR_MESSAGES), 'error');
    } finally {
      setCanceling(false);
    }
  };

  // --- Pagar fatura --------------------------------------------------------
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState<MerchantInvoiceDto | null>(null);
  const [charge, setCharge] = useState<MerchantInvoiceChargeDto | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const openPayModal = async (invoice: MerchantInvoiceDto) => {
    setPayingInvoice(invoice);
    setCharge(null);
    setPayError(null);
    setPayModalOpen(true);
    setPayLoading(true);
    try {
      const result = await adminApi.payInvoice(invoice.id);
      setCharge(result);
    } catch (err) {
      setPayError(extractErrorMessage(err, 'Não foi possível gerar a cobrança.', BILLING_ERROR_MESSAGES));
    } finally {
      setPayLoading(false);
    }
  };

  const copyPixCode = () => {
    if (!charge?.pixCopyPaste || !navigator.clipboard) return;
    navigator.clipboard.writeText(charge.pixCopyPaste);
    showNotification('Código Pix copiado.', 'success');
  };

  const canPay = (invoice: MerchantInvoiceDto) => invoice.status === 'OPEN' || invoice.status === 'OVERDUE';

  return (
    <>
      <PageHeader
        title="Financeiro"
        subtitle="Acompanhe sua assinatura e o histórico de faturas do Kardume Urbano."
      />

      <Section>
        <Card>
          <CardHeader>
            <h3>Assinatura atual</h3>
          </CardHeader>
          <CardBody>
            <DataState
              loading={subscription.loading}
              error={subscription.error}
              onRetry={subscription.reload}
            >
              {subscription.data && (
                <>
                  <SummaryGrid>
                    <SummaryItem>
                      <span>Plano</span>
                      <strong>{subscription.data.planName ?? '-'}</strong>
                      {subscription.data.scheduledPrice != null && (
                        <small>
                          A partir do próximo ciclo: {formatCurrency(subscription.data.scheduledPrice)}
                          {subscription.data.scheduledBillingPeriod
                            ? ` / ${BILLING_PERIOD_LABELS[subscription.data.scheduledBillingPeriod]}`
                            : ''}
                        </small>
                      )}
                    </SummaryItem>
                    <SummaryItem>
                      <span>Situação</span>
                      <div>
                        <Badge tone={subscriptionTone(subscription.data.status)}>
                          {SUBSCRIPTION_STATUS_LABELS[subscription.data.status]}
                        </Badge>
                      </div>
                      {subscription.data.graceUntil && (
                        <small>Carência até {formatDate(subscription.data.graceUntil)}</small>
                      )}
                    </SummaryItem>
                    <SummaryItem>
                      <span>Valor</span>
                      <strong>
                        {formatCurrency(subscription.data.price)} /{' '}
                        {BILLING_PERIOD_LABELS[subscription.data.billingPeriod]}
                      </strong>
                    </SummaryItem>
                    <SummaryItem>
                      <span>Período vigente</span>
                      <strong>
                        {formatDate(subscription.data.currentPeriodStart)} –{' '}
                        {formatDate(subscription.data.currentPeriodEnd)}
                      </strong>
                    </SummaryItem>
                    <SummaryItem>
                      <span>Próxima cobrança</span>
                      <strong>
                        {subscription.data.nextInvoiceAt
                          ? formatDate(subscription.data.nextInvoiceAt)
                          : '-'}
                      </strong>
                    </SummaryItem>
                  </SummaryGrid>

                  {canManage && subscription.data.status !== 'CANCELED' && (
                    <ActionsRow>
                      <Button variant="outline" onClick={openPlanModal}>
                        Trocar de plano
                      </Button>
                      <Button variant="danger" onClick={() => setCancelModalOpen(true)}>
                        Cancelar assinatura
                      </Button>
                    </ActionsRow>
                  )}
                </>
              )}
            </DataState>
          </CardBody>
        </Card>
      </Section>

      <Card>
        <CardHeader>
          <h3>Faturas</h3>
        </CardHeader>
        <CardBody>
          <DataState
            loading={invoices.loading}
            error={invoices.error}
            isEmpty={!invoices.data || invoices.data.content.length === 0}
            emptyTitle="Nenhuma fatura emitida"
            emptyDescription="Suas faturas aparecerão aqui assim que forem geradas."
            onRetry={invoices.reload}
          >
            {invoices.data && (
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <th>Plano</th>
                      <th>Valor</th>
                      <th>Situação</th>
                      <th>Vencimento</th>
                      <th>Pagamento</th>
                      <th>Fatura</th>
                      {canManage && <th>Ação</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.data.content.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>{invoice.planName}</td>
                        <td>{formatCurrency(invoice.amount)}</td>
                        <td>
                          <Badge tone={invoiceTone(invoice.status)}>
                            {INVOICE_STATUS_LABELS[invoice.status]}
                          </Badge>
                        </td>
                        <td>{formatDate(invoice.dueDate)}</td>
                        <td>{invoice.paidAt ? formatDate(invoice.paidAt) : '-'}</td>
                        <td>
                          {invoice.invoiceUrl ? (
                            <InvoiceLink
                              href={invoice.invoiceUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <ExternalLink size={14} /> Ver fatura
                            </InvoiceLink>
                          ) : (
                            '-'
                          )}
                        </td>
                        {canManage && (
                          <td>
                            {canPay(invoice) ? (
                              <Button variant="outline" onClick={() => openPayModal(invoice)}>
                                Pagar
                              </Button>
                            ) : (
                              '-'
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
            )}
          </DataState>
        </CardBody>
      </Card>

      {/* Trocar de plano */}
      <Modal
        isOpen={planModalOpen}
        onClose={() => !changingPlan && setPlanModalOpen(false)}
        title="Trocar de plano"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPlanModalOpen(false)} disabled={changingPlan}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={confirmChangePlan}
              isLoading={changingPlan}
              disabled={!selectedPlanId}
            >
              Confirmar troca
            </Button>
          </>
        }
      >
        <DataState loading={plansLoading} error={null}>
          <PlanList>
            {(plans ?? []).map((plan) => (
              <PlanOption
                key={plan.id}
                type="button"
                $selected={selectedPlanId === plan.id}
                $disabled={plan.current}
                disabled={plan.current}
                onClick={() => setSelectedPlanId(plan.id)}
              >
                <div>
                  <strong>
                    {plan.name} {plan.current && '(plano atual)'}
                  </strong>
                  <span>
                    {formatCurrency(plan.price)} / {BILLING_PERIOD_LABELS[plan.billingPeriod]}
                    {plan.description ? ` - ${plan.description}` : ''}
                  </span>
                </div>
                <PlanCheck $selected={selectedPlanId === plan.id}>
                  {selectedPlanId === plan.id && <Check size={14} />}
                </PlanCheck>
              </PlanOption>
            ))}
          </PlanList>
        </DataState>
        <Hint>
          A troca vale a partir do próximo ciclo - o valor deste ciclo não muda. Em downgrade, a
          diferença já paga vira crédito proporcional para o próximo ciclo.
        </Hint>
      </Modal>

      {/* Cancelar assinatura */}
      <ConfirmModal
        isOpen={cancelModalOpen}
        title="Cancelar assinatura"
        message="A assinatura será encerrada. Você pode continuar usando o painel normalmente até o fim do período já pago; depois disso, o acesso às funcionalidades do plano é interrompido."
        confirmLabel="Cancelar assinatura"
        cancelLabel="Manter assinatura"
        danger
        loading={canceling}
        onConfirm={confirmCancel}
        onClose={() => setCancelModalOpen(false)}
      />

      {/* Pagar fatura */}
      <Modal
        isOpen={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        title="Pagar fatura"
        footer={
          <Button variant="secondary" onClick={() => setPayModalOpen(false)}>
            Fechar
          </Button>
        }
      >
        <DataState loading={payLoading} error={payError}>
          {charge && (
            <PixBox>
              {charge.pixQrCodeImage && (
                <PixImage src={`data:image/png;base64,${charge.pixQrCodeImage}`} alt="QR Code Pix" />
              )}
              {charge.pixCopyPaste && (
                <PixCodeRow>
                  <code>{charge.pixCopyPaste}</code>
                  <Button variant="outline" onClick={copyPixCode} aria-label="Copiar código Pix">
                    <Copy size={14} />
                  </Button>
                </PixCodeRow>
              )}
              {charge.invoiceUrl && (
                <InvoiceLink href={charge.invoiceUrl} target="_blank" rel="noreferrer">
                  <ExternalLink size={14} /> Ver fatura hospedada
                </InvoiceLink>
              )}
              <Hint>
                Pagamento de {payingInvoice ? formatCurrency(payingInvoice.amount) : ''} aguardando
                confirmação. Assim que o pagamento for identificado pelo provedor, a fatura é
                automaticamente marcada como paga - isso não acontece na hora.
              </Hint>
            </PixBox>
          )}
        </DataState>
      </Modal>
    </>
  );
};

export default BillingPage;
